import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Users, Calendar, Loader2, X, CheckCircle, XCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STATUS_STYLES = {
  active: "border-[#22C55E]/30 text-[#16A34A] bg-[#22C55E]/8",
  pending: "border-[#F59E0B]/30 text-[#D97706] bg-[#F59E0B]/8",
  approved: "border-[#22C55E]/30 text-[#16A34A] bg-[#22C55E]/8",
  rejected: "border-[#FF3636]/30 text-[#DC2626] bg-[#FF3636]/8",
};

export default function HRModule() {
  const [employees, setEmployees] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("employees");
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ employee_id: "", name: "", email: "", department: "", designation: "", joining_date: "", salary: "" });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [e, l, s] = await Promise.all([
        axios.get(`${API}/employees`, { withCredentials: true }),
        axios.get(`${API}/leave-requests`, { withCredentials: true }),
        axios.get(`${API}/employees/stats`, { withCredentials: true }),
      ]);
      setEmployees(e.data); setLeaves(l.data); setStats(s.data);
    } catch {} finally { setLoading(false); }
  };

  const handleLeave = async (id, status) => {
    await axios.patch(`${API}/leave-requests/${id}`, { status }, { withCredentials: true });
    fetchAll();
  };

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/employees`, { ...form, salary: parseFloat(form.salary) }, { withCredentials: true });
      setShowModal(false); fetchAll();
    } catch {}
  };

  return (
    <div className="space-y-6" data-testid="hr-module">
      <div className="flex items-start justify-between border-b border-[#D8D4CB] pb-5">
        <div>
          <p className="font-['Syne'] text-[10px] font-bold uppercase tracking-[0.3em] text-[#BDBAA8] mb-1">Human Resources</p>
          <h2 className="font-['Syne'] font-bold text-2xl text-[#0C0C10]">HR Management</h2>
        </div>
        {activeTab === "employees" && (
          <button onClick={() => setShowModal(true)} data-testid="add-employee-btn"
            className="bg-[#0C0C10] text-[#EAFF00] font-['Syne'] font-bold text-[10px] uppercase tracking-widest px-5 py-3 hover:bg-[#1C1C22] transition-colors flex items-center gap-2">
            <Users className="w-3.5 h-3.5" /> Add Employee
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Total Staff", value: stats.total || employees.length || 0, color: "#F59E0B" },
          { label: "Active", value: stats.active || 0, color: "#22C55E" },
          { label: "On Leave", value: stats.on_leave || 0, color: "#0A7AFF" },
          { label: "Pending Leaves", value: stats.pending_leaves || leaves.filter(l => l.status === "pending").length, color: "#FF3636" },
        ].map((s, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
            className="bg-white border border-[#E2DDD4] p-5" style={{ borderLeft: `3px solid ${s.color}` }}>
            <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8] mb-2">{s.label}</p>
            <p className="font-['JetBrains_Mono'] font-bold text-3xl text-[#0C0C10]">{s.value}</p>
          </motion.div>
        ))}
      </div>

      <div className="flex gap-2 border-b border-[#D8D4CB]">
        {[["employees", "Employees", Users], ["leaves", "Leave Requests", Calendar]].map(([t, label, Icon]) => (
          <button key={t} onClick={() => setActiveTab(t)} data-testid={`hr-tab-${t}`}
            className={`flex items-center gap-2 font-['Syne'] font-bold text-[9px] uppercase tracking-widest px-5 py-3 border-b-2 transition-all ${activeTab === t ? "border-[#0C0C10] text-[#0C0C10]" : "border-transparent text-[#BDBAA8]"}`}>
            <Icon className="w-3.5 h-3.5" /> {label} {t === "leaves" && leaves.filter(l => l.status === "pending").length > 0 && <span className="w-4 h-4 bg-[#FF3636] text-white rounded-full text-[8px] flex items-center justify-center font-bold">{leaves.filter(l => l.status === "pending").length}</span>}
          </button>
        ))}
      </div>

      {activeTab === "employees" && (
        <div className="bg-white border border-[#E2DDD4]">
          <div className="grid gap-0 px-5 py-3 border-b border-[#E2DDD4] bg-[#FAFAF7]" style={{ gridTemplateColumns: "60px 1.5fr 1fr 1.5fr 80px 100px 80px" }}>
            {["ID", "Name", "Designation", "Department", "Joined", "Salary", "Status"].map(h => (
              <span key={h} className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8]">{h}</span>
            ))}
          </div>
          {loading ? <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-[#BDBAA8]" /></div>
            : employees.map((emp, i) => (
            <div key={emp._id} className="grid gap-0 px-5 py-3.5 border-b border-[#F0ECE4] last:border-0 hover:bg-[#FAFAF7] items-center" style={{ gridTemplateColumns: "60px 1.5fr 1fr 1.5fr 80px 100px 80px" }}>
              <span className="font-['JetBrains_Mono'] text-[10px] text-[#BDBAA8]">{emp.employee_id}</span>
              <div>
                <p className="font-['Syne'] font-bold text-xs text-[#0C0C10]">{emp.name}</p>
                <p className="font-['DM_Sans'] text-[10px] text-[#BDBAA8]">{emp.email}</p>
              </div>
              <span className="font-['DM_Sans'] text-xs text-[#0C0C10]">{emp.designation}</span>
              <span className="font-['DM_Sans'] text-xs text-[#7A7468]">{emp.department}</span>
              <span className="font-['DM_Sans'] text-[10px] text-[#BDBAA8]">{emp.joining_date?.slice(0, 10)}</span>
              <span className="font-['JetBrains_Mono'] text-xs font-bold text-[#0C0C10]">₹{(emp.salary/1000).toFixed(0)}K</span>
              <span className={`font-['Syne'] font-bold text-[9px] uppercase px-2 py-1 border w-fit ${STATUS_STYLES[emp.status]}`}>{emp.status}</span>
            </div>
          ))}
        </div>
      )}

      {activeTab === "leaves" && (
        <div className="bg-white border border-[#E2DDD4]">
          <div className="grid gap-0 px-5 py-3 border-b border-[#E2DDD4] bg-[#FAFAF7]" style={{ gridTemplateColumns: "1.5fr 1fr 1fr 80px 80px 100px" }}>
            {["Employee", "Leave Type", "Period", "Days", "Status", "Actions"].map(h => (
              <span key={h} className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8]">{h}</span>
            ))}
          </div>
          {leaves.map((l, i) => (
            <div key={l._id} className="grid gap-0 px-5 py-3.5 border-b border-[#F0ECE4] last:border-0 hover:bg-[#FAFAF7] items-center" style={{ gridTemplateColumns: "1.5fr 1fr 1fr 80px 80px 100px" }}>
              <div>
                <p className="font-['Syne'] font-bold text-xs text-[#0C0C10]">{l.employee_name}</p>
                <p className="font-['DM_Sans'] text-[10px] text-[#BDBAA8]">{l.reason}</p>
              </div>
              <span className="font-['DM_Sans'] text-xs text-[#0C0C10]">{l.leave_type}</span>
              <span className="font-['DM_Sans'] text-[10px] text-[#7A7468]">{l.from_date} → {l.to_date}</span>
              <span className="font-['JetBrains_Mono'] text-xs font-bold text-[#0C0C10]">{l.days}d</span>
              <span className={`font-['Syne'] font-bold text-[9px] uppercase px-2 py-1 border w-fit ${STATUS_STYLES[l.status]}`}>{l.status}</span>
              {l.status === "pending" ? (
                <div className="flex gap-1.5">
                  <button onClick={() => handleLeave(l._id, "approved")} data-testid={`approve-${i}`} className="p-1.5 text-[#22C55E] border border-[#22C55E]/25 hover:bg-[#22C55E]/10 transition-colors"><CheckCircle className="w-3.5 h-3.5" /></button>
                  <button onClick={() => handleLeave(l._id, "rejected")} data-testid={`reject-${i}`} className="p-1.5 text-[#FF3636] border border-[#FF3636]/25 hover:bg-[#FF3636]/10 transition-colors"><XCircle className="w-3.5 h-3.5" /></button>
                </div>
              ) : <span className="font-['DM_Sans'] text-[10px] text-[#BDBAA8]">—</span>}
            </div>
          ))}
        </div>
      )}

      <AnimatePresence>
        {showModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-[#F5F2EA] max-w-md w-full p-8">
              <div className="flex justify-between mb-6">
                <h3 className="font-['Syne'] font-bold text-lg text-[#0C0C10]">Add Employee</h3>
                <button onClick={() => setShowModal(false)}><X className="w-5 h-5 text-[#BDBAA8]" /></button>
              </div>
              <form onSubmit={handleAddEmployee} className="space-y-4">
                {[["employee_id", "Employee ID"], ["name", "Full Name"], ["email", "Email"], ["department", "Department"], ["designation", "Designation"], ["joining_date", "Joining Date"], ["salary", "Monthly Salary (₹)"]].map(([f, l]) => (
                  <div key={f}>
                    <label className="block font-['Syne'] text-[9px] font-bold uppercase tracking-[0.2em] text-[#7A7468] mb-1.5">{l}</label>
                    <input type={f === "joining_date" ? "date" : f === "salary" ? "number" : "text"} value={form[f]} onChange={e => setForm(p => ({...p, [f]: e.target.value}))} required
                      className="w-full bg-transparent border-0 border-b border-[#D8D4CB] pb-2 text-sm font-['DM_Sans'] text-[#0C0C10] focus:outline-none focus:border-[#0C0C10] transition-colors" />
                  </div>
                ))}
                <button type="submit" className="w-full bg-[#0C0C10] text-[#EAFF00] font-['Syne'] font-bold text-xs uppercase tracking-widest py-3.5 hover:bg-[#1C1C22] transition-colors mt-2">Add Employee</button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
